import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useBranch } from "@/context/BranchContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertTriangle } from "lucide-react";
import { TestDataCleanup } from "@/components/TestDataCleanup";
import { toast } from "sonner";

export default function Settings() {
  const { isSuperAdmin } = useAuth();
  const { warehouses = [] } = useBranch();
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const doReset = async () => {
    setResetting(true);
    try {
      const { data } = await api.post("/admin/reset-data", { password: resetPw, confirm: resetConfirm });
      toast.success(`Clean reset complete — ${data.total} record(s) cleared`);
      setResetOpen(false); setResetPw(""); setResetConfirm("");
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(apiError(e)); }
    finally { setResetting(false); }
  };

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-muted-foreground">Loading…</div>;

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        currency: s.currency, tax_rate: Number(s.tax_rate), near_expiry_days: Number(s.near_expiry_days),
        low_stock_default: Number(s.low_stock_default), fefo_enforced: s.fefo_enforced,
        max_discount_percent: Number(s.max_discount_percent), allow_negative_stock: s.allow_negative_stock,
        enforce_credit_limit: s.enforce_credit_limit,
        markup_cash_percent: Number(s.markup_cash_percent), markup_corporate_percent: Number(s.markup_corporate_percent),
        near_expiry_discount_percent: Number(s.near_expiry_discount_percent),
        shift_variance_threshold: Number(s.shift_variance_threshold) || 0,
        wholesale_branch_id: s.wholesale_branch_id || null,
      });
      toast.success("Business rules saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl">
      <PageHeader title="Business Rules" subtitle="Configurable operational policies — applied across the whole network" />
      <Card className="p-6 shadow-sm space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Currency</Label><Input data-testid="setting-currency" value={s.currency} onChange={(e) => set("currency", e.target.value)} /></div>
          <div><Label>Tax / VAT rate (e.g. 0.15 = 15%)</Label><Input type="number" step="0.01" data-testid="setting-tax" value={s.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} /></div>
          <div><Label>Near-expiry window (days)</Label><Input type="number" data-testid="setting-expiry-days" value={s.near_expiry_days} onChange={(e) => set("near_expiry_days", e.target.value)} /></div>
          <div><Label>Default low-stock level</Label><Input type="number" data-testid="setting-low-stock" value={s.low_stock_default} onChange={(e) => set("low_stock_default", e.target.value)} /></div>
          <div><Label>Max discount (%)</Label><Input type="number" data-testid="setting-max-discount" value={s.max_discount_percent} onChange={(e) => set("max_discount_percent", e.target.value)} /></div>
          <div><Label>Near-expiry discount (%)</Label><Input type="number" step="0.1" data-testid="setting-nearexpiry-discount" value={s.near_expiry_discount_percent ?? 20} onChange={(e) => set("near_expiry_discount_percent", e.target.value)} /></div>
          <div><Label>Cash markup (%)</Label><Input type="number" step="0.1" data-testid="setting-markup-cash" value={s.markup_cash_percent} onChange={(e) => set("markup_cash_percent", e.target.value)} /></div>
          <div><Label>Corporate markup (%)</Label><Input type="number" step="0.1" data-testid="setting-markup-corporate" value={s.markup_corporate_percent} onChange={(e) => set("markup_corporate_percent", e.target.value)} /></div>
          <div><Label>Partner Supply main warehouse</Label>
            <Select value={s.wholesale_branch_id || "default"} onValueChange={(v) => set("wholesale_branch_id", v === "default" ? null : v)}>
              <SelectTrigger data-testid="setting-wholesale-branch"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Not set — uses first warehouse</SelectItem>
                {warehouses.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Shift cash variance alert threshold (GHS)</Label><Input type="number" step="0.01" data-testid="setting-shift-variance" value={s.shift_variance_threshold ?? 0} onChange={(e) => set("shift_variance_threshold", e.target.value)} /></div>
        </div>
        <div className="rounded-lg p-3 text-xs" style={{ background: "var(--mm-surface-raised)", color: "var(--mm-text-2)" }}>
          Selling price = cost × (1 + markup). Cash and corporate markups apply network-wide. <span className="font-semibold">Each insurance company has its own markup</span>, set per scheme under Customers → Insurance schemes.
        </div>
        <Toggle label="Enforce FEFO dispensing" desc="Always allocate stock First-Expiry-First-Out" checked={s.fefo_enforced} onChange={(v) => set("fefo_enforced", v)} testid="setting-fefo" />
        <Toggle label="Enforce corporate credit limits" desc="Block sales that exceed a customer's credit limit" checked={s.enforce_credit_limit} onChange={(v) => set("enforce_credit_limit", v)} testid="setting-credit" />
        <Toggle label="Allow negative stock" desc="Permit dispensing when on-hand is insufficient" checked={s.allow_negative_stock} onChange={(v) => set("allow_negative_stock", v)} testid="setting-negative" />
        <div className="pt-2"><Button data-testid="save-settings-btn" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Business Rules"}</Button></div>
      </Card>

      {isSuperAdmin && (
        <Card className="p-6 shadow-sm mt-6 border-rose-300 dark:border-rose-900">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-rose-700 dark:text-rose-400">Danger zone — Reset to clean data</div>
              <p className="text-sm text-muted-foreground mt-1">
                Permanently deletes all trial transactions — sales, inventory stock & movements, transfers, stocktakes, adjustments,
                warehouse daily entries, deliveries, prescriptions, claims, returns, shifts, <strong>customers and insurance schemes</strong>,
                and resets all counters. Your product catalog, categories, suppliers, users, roles, branches, business rules and the audit log are kept.
                Use this once before go-live.
              </p>
              <Button variant="outline" className="mt-3 text-rose-600 border-rose-300 hover:bg-rose-50" data-testid="open-reset-data-btn" onClick={() => { setResetPw(""); setResetConfirm(""); setResetOpen(true); }}>
                Reset to clean data
              </Button>
              <Button variant="outline" className="mt-3 ml-2" data-testid="open-test-data-btn" onClick={() => setCleanupOpen(true)}>
                Remove test users, branches &amp; products
              </Button>
            </div>
          </div>
        </Card>
      )}

      <TestDataCleanup open={cleanupOpen} onOpenChange={setCleanupOpen} />
      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-rose-700">Confirm clean-data reset</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">This cannot be undone. All trial transactions, customers and insurance schemes will be erased and inventory reset to zero.</p>
            <div><Label>Re-enter your password</Label><Input type="password" data-testid="reset-password-input" value={resetPw} onChange={(e) => setResetPw(e.target.value)} /></div>
            <div><Label>Type <span className="font-mono font-semibold">RESET</span> to confirm</Label><Input data-testid="reset-confirm-input" value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-reset-data-btn" disabled={resetting || resetConfirm.trim() !== "RESET" || !resetPw} onClick={doReset}>{resetting ? "Resetting…" : "Erase & reset"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Toggle({ label, desc, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between border-t border-border pt-4">
      <div><div className="font-medium text-foreground">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}
